// src/hooks/useDeletePin.js
//
// Централизованное подтверждение опасных удалений через PIN.
//
// Использование:
//   const { confirm, PinModal } = useDeletePin();
//
//   // В обработчике удаления:
//   const ok = await confirm({ title: 'Удалить товар X?', message: '...' });
//   if (!ok) return;
//   // ... удаление ...
//
//   // В JSX:
//   {PinModal}
//
// Логика:
//   - Если PIN не установлен на сервере — показывает обычный confirm-диалог
//     (с заголовком и сообщением, без поля ввода)
//   - Если установлен — модалка с цифровым полем, проверка через POST verify
//   - После успешного ввода — PIN считается «подтверждённым» на 5 минут
//     (для текущей сессии в RAM), повторно не спрашивается
//   - Сброс памяти при перезагрузке приложения

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shop as shopApi } from '../api';
import Icon from '../components/Icon';

const REMEMBER_MS = 5 * 60 * 1000; // 5 минут

// Глобальная память — общая для всех экземпляров хука в одном сеансе
const memory = {
  pinSet: null,           // bool | null (null = ещё не загружено)
  lastVerifiedAt: 0,      // timestamp ms
};

export function useDeletePin() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [params, setParams] = useState({ title: '', message: '' });
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Promise который резолвится результатом подтверждения
  const resolverRef = useRef(null);

  // Подгружаем статус PIN при первом использовании
  useEffect(() => {
    if (memory.pinSet === null) {
      shopApi.pinStatus()
        .then(r => { memory.pinSet = !!r.pinSet; })
        .catch(() => { memory.pinSet = false; });
    }
  }, []);

  const isRemembered = () => {
    return Date.now() - memory.lastVerifiedAt < REMEMBER_MS;
  };

  const confirm = useCallback(({ title, message } = {}) => {
    return new Promise((resolve) => {
      // Если PIN не установлен — обычный confirm-диалог (без PIN)
      // Используем модалку с кнопками без поля для PIN
      if (memory.pinSet === false) {
        setParams({ title: title || t('common.delete') + '?', message: message || '', noPin: true });
        setPin('');
        setError('');
        setOpen(true);
        resolverRef.current = resolve;
        return;
      }
      // Если PIN установлен и уже введён недавно — пропускаем
      if (memory.pinSet === true && isRemembered()) {
        resolve(true);
        return;
      }
      // Открываем модалку с вводом PIN
      setParams({ title: title || t('deletePin.confirmTitle'), message: message || '', noPin: false });
      setPin('');
      setError('');
      setOpen(true);
      resolverRef.current = resolve;
    });
  }, [t]);

  const close = (result) => {
    setOpen(false);
    setPin('');
    setError('');
    setBusy(false);
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
  };

  const onSubmit = async () => {
    if (params.noPin) {
      // Подтверждение без PIN
      close(true);
      return;
    }
    if (!/^\d{4}$/.test(pin)) {
      setError(t('deletePin.errPinFormat'));
      return;
    }
    setBusy(true);
    setError('');
    try {
      const r = await shopApi.pinVerify(pin);
      if (r.valid) {
        memory.lastVerifiedAt = Date.now();
        close(true);
      } else {
        setError(t('deletePin.errWrongPin'));
        setPin('');
        setBusy(false);
      }
    } catch (e) {
      setError(e?.message || t('common.error'));
      setBusy(false);
    }
  };

  const onCancel = () => close(false);

  const tapKey = (digit) => {
    if (busy) return;
    if (pin.length >= 4) return;
    const next = pin + digit;
    setPin(next);
    setError('');
    // Автоподтверждение при 4 цифрах
    if (next.length === 4) {
      setTimeout(() => onSubmitAuto(next), 50);
    }
  };
  const tapBackspace = () => {
    if (busy) return;
    setPin(pin.slice(0, -1));
    setError('');
  };
  const onSubmitAuto = async (currentPin) => {
    if (currentPin.length !== 4) return;
    setBusy(true);
    setError('');
    try {
      const r = await shopApi.pinVerify(currentPin);
      if (r.valid) {
        memory.lastVerifiedAt = Date.now();
        close(true);
      } else {
        setError(t('deletePin.errWrongPin'));
        setPin('');
        setBusy(false);
      }
    } catch (e) {
      setError(e?.message || t('common.error'));
      setBusy(false);
    }
  };

  const PinModal = open ? (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg)',
          width: '100%', maxWidth: 420,
          borderRadius: 'var(--r-lg) var(--r-lg) 0 0',
          padding: 20,
          maxHeight: '90vh', overflow: 'auto',
        }}
      >
        {/* Заголовок */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
          <div style={{
            width: 40, height: 40, borderRadius: '50%',
            background: 'var(--danger-soft)', color: 'var(--danger)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            marginRight: 12, flexShrink: 0,
          }}>
            <Icon name="trash" size={20} />
          </div>
          <h2 style={{ fontSize: 16, fontWeight: 700, flex: 1 }}>
            {params.title}
          </h2>
        </div>
        {params.message && (
          <p style={{ fontSize: 13, color: 'var(--fg-muted)', marginBottom: 16, lineHeight: 1.4 }}>
            {params.message}
          </p>
        )}

        {/* PIN-поле или просто пустая полоса для подтверждения без PIN */}
        {!params.noPin && (
          <>
            <p style={{ fontSize: 11, color: 'var(--fg-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8, fontWeight: 700 }}>
              {t('deletePin.enterPin')}
            </p>
            {/* Точки PIN */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: 14, marginBottom: 12 }}>
              {[0,1,2,3].map(i => (
                <div key={i} style={{
                  width: 16, height: 16, borderRadius: '50%',
                  background: i < pin.length ? 'var(--brand-primary)' : 'var(--border)',
                  transition: 'background 0.1s',
                }} />
              ))}
            </div>
            {error && (
              <p style={{ fontSize: 12, color: 'var(--danger)', textAlign: 'center', marginBottom: 8 }}>
                {error}
              </p>
            )}
            {/* Цифровая клавиатура */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 12 }}>
              {[1,2,3,4,5,6,7,8,9].map(d => (
                <button
                  key={d}
                  onClick={() => tapKey(String(d))}
                  disabled={busy}
                  style={pinKeyStyle}
                >
                  {d}
                </button>
              ))}
              <div />
              <button onClick={() => tapKey('0')} disabled={busy} style={pinKeyStyle}>0</button>
              <button onClick={tapBackspace} disabled={busy} style={pinKeyStyle}>⌫</button>
            </div>
          </>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={onCancel}
            disabled={busy}
            className="btn btn-ghost"
            style={{ flex: 1 }}
          >
            {t('common.cancel')}
          </button>
          {params.noPin && (
            <button
              onClick={onSubmit}
              disabled={busy}
              className="btn"
              style={{ flex: 1, background: 'var(--danger)', color: '#fff' }}
            >
              {t('common.delete')}
            </button>
          )}
        </div>
      </div>
    </div>
  ) : null;

  return { confirm, PinModal };
}

const pinKeyStyle = {
  height: 56,
  background: 'var(--bg-elevated)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--r-sm)',
  fontSize: 22, fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'var(--font-display)',
};
